import { Button, Label } from "flowbite-react";
import { useState } from "react";
import { useGetGroupsQuery, useCreateGroupMutation } from "@/api/groupApi/group";
import { Group } from "@/sharedTypes";
import GroupFormDialog from "@/components/groups/GroupFormDialog";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import { HiPlus, HiX } from "react-icons/hi";
import { useTranslation } from "react-i18next";

interface GroupSelectProps {
  value?: number[];
  onChange: (value: number[]) => void;
  error?: string;
  label?: string;
}

export default function GroupSelect({
  value = [],
  onChange,
  error,
  label,
}: GroupSelectProps) {
  const { t } = useTranslation(["agent", "group", "common"]);
  const displayLabel = label || t("agent:agentGroup");
  const { data: groupsData, isLoading } = useGetGroupsQuery({
    pagination: { page: 1, pageSize: 100 },
    filters: {},
  });
  const [createGroup] = useCreateGroupMutation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleCreateGroup = (data: Group) => {
    createGroup(data)
      .unwrap()
      .then((res: any) => {
        toast.success(t("group:createSuccess"));
        setIsCreateDialogOpen(false);
        const groupId = res?.group_id || res?.data?.group_id;
        if (groupId) {
          onChange([...value, groupId]);
        }
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const groups: Group[] = groupsData?.items || [];
  const availableGroups = groups.filter((g) => !value.includes(g.group_id));

  const handleAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value, 10);
    if (id && !value.includes(id)) {
      onChange([...value, id]);
    }
    e.target.value = "";
  };

  const handleRemove = (id: number) => {
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label value={displayLabel} />
        <Button
          size="xs"
          color="light"
          onClick={() => setIsCreateDialogOpen(true)}
          title={t("agent:createGroup")}
        >
          <HiPlus className="h-4 w-4" />
        </Button>
      </div>

      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((id) => {
            const group = groups.find((g) => g.group_id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-100 px-2.5 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
              >
                {group?.group_name ?? `Group ${id}`}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  className="inline-flex items-center rounded-sm p-0.5 text-blue-400 hover:bg-blue-200 hover:text-blue-900 dark:hover:bg-blue-800 dark:hover:text-blue-300"
                >
                  <HiX className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <select
        className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
        onChange={handleAdd}
        defaultValue=""
        disabled={isLoading || availableGroups.length === 0}
      >
        <option value="" disabled>
          {t("agent:assignToGroup")}
        </option>
        {availableGroups.map((group) => (
          <option key={group.group_id} value={group.group_id}>
            {group.group_name}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <GroupFormDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateGroup}
      />
    </div>
  );
}
